import { useState } from "react";

import Form, { FormComponentProps } from "@ant-design/compatible/es/form";
import { LockOutlined } from "@ant-design/icons";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { TimezoneKey } from "@impero-com/common/utils/timezone";
import {
    Button,
    FormLabel,
    Modal,
    Section,
    SectionContent,
    SectionGroup,
    SectionHeader,
} from "@impero-com/ui";
import { Select } from "antd";
import i18next from "i18next";
import { useParams } from "react-router-dom";

import type { UserProfile } from "api/generated/model";
import { Locale, ChangeUserEmailBody, UserViewDto } from "api/generated/model";
import { CheckboxInput } from "components/form/legacyFields/checkboxInput";
import { SelectInput } from "components/form/legacyFields/selectInput";
import { StringInput } from "components/form/legacyFields/stringInput";
import { OAuthConnectionsSection } from "components/oauthConnections/OAuthConnectionsSection";
import { TimezoneTypeahead } from "components/timezoneTypeahead/timezoneTypeahead";
import { NotificationFrequencySettings } from "org/profile/NotificationFrequencySettings";
import { NotificationPreferences } from "org/profile/NotificationPreferences";
import { useCulturesQuery } from "org/profile/queries/useCulturesQuery";
import { useUpdateUserMutation } from "org/profile/queries/useUpdateUserMutation";
import { useUpdateUserSettingsMutation } from "org/profile/queries/useUpdateUserSettingsMutation";
import { useUserSettingsQuery } from "org/profile/queries/useUserSettingsQuery";
import { emailValidator } from "utils/form";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import { getCultureInformationByName } from "utils/locale";

import { ENVIRONMENT } from "../../env";

import { useUpdateUserEmailMutation } from "./queries/useUpdateUserEmailMutation";
import { useUserWithDetailsQuery } from "./queries/useUserWithDetailsQuery";
import RoleAssignment from "./roleAssignment";
import * as theme from "./theme.css";

const PASSWORD_AUTHENTICATION_ENABLED_VALUE = "enabled";

interface UserDetailsProps {
    orgProfileCookie: UserProfile | null;
}

export function UserDetails({ orgProfileCookie }: UserDetailsProps) {
    const { id: userIdParam } = useParams();

    if (!userIdParam) {
        throw new Error("User ID is required.");
    }
    if (!orgProfileCookie) {
        console.error("Failed to retrieve profile");
        return null;
    }

    return (
        <UserDetailsForm
            userId={Number(userIdParam)}
            loggedInUserId={orgProfileCookie.userId}
        />
    );
}

interface UserDetailsFormProps {
    userId: number;
    loggedInUserId: number;
}

function UserDetailsForm({ userId, loggedInUserId }: UserDetailsFormProps) {
    const { t } = useTranslation();
    const notificationSettingsEnabled = useFeatureFlag(
        "userNotificationSettings"
    );
    const notificationFrequencyEnabled = useFeatureFlag(
        "userNotificationFrequency"
    );
    const [changeEmailOpen, setChangeEmailOpen] = useState(false);

    const { data: userWithDetails } = useUserWithDetailsQuery(userId);
    const { data: cultures } = useCulturesQuery();
    const { data: userSettings } = useUserSettingsQuery(userId);

    const updateUser = useUpdateUserMutation();
    const updateUserSettings = useUpdateUserSettingsMutation();
    const updateUserEmail = useUpdateUserEmailMutation();

    const {
        content: { resource: user, actions },
        linkedResources: { roles, isPasswordSet },
    } = userWithDetails;
    const {
        editableRoles,
        editEmail: canEditEmail,
        manageOauthConnections: canManageOauthConnections,
    } = actions;

    const cultureOptions = cultures.map((culture) => {
        const cultureInfo = getCultureInformationByName(culture.name);
        return cultureInfo
            ? { label: cultureInfo.label, value: cultureInfo.name }
            : { label: culture.name, value: culture.name };
    });

    // Password auth is stored inverted; the checkbox tracks the enabled state.
    const passwordAuthenticationEnabled =
        !userSettings.passwordAuthenticationDisabled;

    return (
        <>
            {changeEmailOpen && (
                <UserEmailChange
                    originalEmail={user.email}
                    updateUserEmail={(value) =>
                        updateUserEmail
                            .mutateAsync({ userId, data: value })
                            .then((response) => response.data)
                    }
                    closeDialog={() => setChangeEmailOpen(false)}
                />
            )}
            <SectionGroup>
                <Section>
                    <SectionHeader
                        level={4}
                        titleLabel={t("UserManagement_UserDetails_Title")}
                    />
                    <SectionContent>
                        <StringInput
                            id="firstname"
                            label={t("MyProfile_UserDetails_FirstName")}
                            type="EDIT"
                            value={user.firstname}
                            onChange={(firstname) =>
                                updateUser.mutate({
                                    userId,
                                    data: { user: { firstname } },
                                })
                            }
                            validators={[
                                {
                                    kind: "ruleset",
                                    required: true,
                                    whitespace: true,
                                },
                            ]}
                        />
                        <StringInput
                            id="lastname"
                            label={t("MyProfile_UserDetails_LastName")}
                            type="EDIT"
                            value={user.lastname}
                            onChange={(lastname) =>
                                updateUser.mutate({
                                    userId,
                                    data: { user: { lastname } },
                                })
                            }
                            validators={[
                                {
                                    kind: "ruleset",
                                    required: true,
                                    whitespace: true,
                                },
                            ]}
                        />
                        <div className={theme.setEmailContainer}>
                            <StringInput
                                label={t("MyProfile_UserDetails_Email")}
                                type="READ_ONLY"
                                value={user.email}
                            />
                            {canEditEmail && (
                                <Button
                                    id="change-email-button-link"
                                    variant="link"
                                    onClick={() => setChangeEmailOpen(true)}
                                >
                                    {t("User_Change_Email")}
                                </Button>
                            )}
                        </div>
                        <div className={theme.authCheckboxContainer}>
                            <CheckboxInput
                                id="passwordAuthenticationDisabled"
                                label={t("CreateUser_Authentication")}
                                type="EDIT"
                                options={[
                                    {
                                        label: t(
                                            "CreateUser_PasswordAuthenticationEnabled"
                                        ),
                                        value: PASSWORD_AUTHENTICATION_ENABLED_VALUE,
                                    },
                                ]}
                                columns={1}
                                value={
                                    passwordAuthenticationEnabled
                                        ? [
                                              PASSWORD_AUTHENTICATION_ENABLED_VALUE,
                                          ]
                                        : []
                                }
                                onChange={(x) =>
                                    updateUserSettings.mutate({
                                        userId,
                                        data: {
                                            passwordAuthenticationDisabled:
                                                !x?.includes(
                                                    PASSWORD_AUTHENTICATION_ENABLED_VALUE
                                                ),
                                        },
                                    })
                                }
                            />
                            {isPasswordSet && (
                                <span className={theme.userHasPasswordSet}>
                                    <LockOutlined className={theme.lockIcon} />
                                    {t("UserManagement_UserHasSetPassword")}
                                </span>
                            )}
                        </div>
                    </SectionContent>
                </Section>

                <Section>
                    <SectionHeader
                        level={4}
                        titleLabel={t("MyProfile_LanguageAndTimezone_Title")}
                    />
                    <SectionContent>
                        <SelectInput
                            id="culture"
                            label={t("RegisterUser_UpdateUserDetails_Language")}
                            type="EDIT"
                            value={user.culture}
                            options={cultureOptions}
                            onChange={(culture) =>
                                updateUser
                                    .mutateAsync({
                                        userId,
                                        data: {
                                            user: {
                                                culture:
                                                    culture as Locale /* safe because cultureOptions[].value is a locale */,
                                            },
                                        },
                                    })
                                    .then(() => {
                                        if (user.id === loggedInUserId) {
                                            i18next.changeLanguage(
                                                culture || "en"
                                            );
                                        }
                                    })
                            }
                        />
                        <FormLabel>{t("Timezone")}</FormLabel>
                        <TimezoneTypeahead
                            id="timezone"
                            value={user.timezoneName as TimezoneKey}
                            onSelect={(timezoneName) =>
                                updateUser.mutate({
                                    userId,
                                    data: { user: { timezoneName } },
                                })
                            }
                        />
                    </SectionContent>
                </Section>

                {notificationSettingsEnabled && (
                    <NotificationPreferences userId={user.id} />
                )}

                {notificationFrequencyEnabled ? (
                    <NotificationFrequencySettings userId={user.id} />
                ) : (
                    <Section>
                        <SectionHeader
                            level={4}
                            titleLabel={t("MyProfile_EmailGrouping_Title")}
                            subtitleLabel={t(
                                "UserManagement_EmailGrouping_Description"
                            )}
                            showBorder
                        />
                        <SectionContent>
                            <Select
                                id="batch-notifications"
                                className={theme.emailGroupingSelect}
                                value={
                                    userSettings.batchNotifications
                                        ? "one_per_batch"
                                        : "each_control"
                                }
                                onChange={(value) =>
                                    updateUserSettings.mutate({
                                        userId,
                                        data: {
                                            batchNotifications:
                                                value === "one_per_batch",
                                        },
                                    })
                                }
                                options={[
                                    {
                                        value: "one_per_batch",
                                        label: t(
                                            "UserManagement_NotificationSettings_OnePerBatch"
                                        ),
                                    },
                                    {
                                        value: "each_control",
                                        label: t(
                                            "UserManagement_NotificationSettings_EachControl"
                                        ),
                                    },
                                ]}
                            />
                        </SectionContent>
                    </Section>
                )}

                {canManageOauthConnections && (
                    <OAuthConnectionsSection userId={user.id} />
                )}

                <Section>
                    <SectionHeader
                        level={4}
                        titleLabel={t("User_Roles_Title")}
                    />
                    <SectionContent>
                        <RoleAssignment
                            assignableRoles={editableRoles}
                            roles={roles}
                            onChangeRole={(role, selected) => {
                                const withRole = roles.includes(role)
                                    ? roles
                                    : [...roles, role];
                                const newRoles = selected
                                    ? withRole
                                    : roles.filter((r) => r !== role);
                                updateUser.mutate({
                                    userId,
                                    data: { roles: newRoles },
                                });
                            }}
                        />
                    </SectionContent>
                </Section>
            </SectionGroup>
        </>
    );
}

interface UserEmailChangeValues {
    captcha: string;
    email: string;
}

interface UserEmailChangeProps extends FormComponentProps<UserEmailChangeValues> {
    originalEmail: string;
    updateUserEmail: (value: ChangeUserEmailBody) => Promise<UserViewDto>;
    closeDialog: () => void;
}

function UserEmailChangeInternal({
    form,
    originalEmail,
    updateUserEmail,
    closeDialog,
}: UserEmailChangeProps) {
    const { t } = useTranslation();
    const { getFieldDecorator } = form;

    const shouldNotMatchPreviousEmailAddr = (
        newEmail: string
    ): string | undefined => {
        // Case-insensitive matching
        if (newEmail.toUpperCase() === originalEmail.toUpperCase()) {
            return t("Email_Matches_Previous_One");
        }
        return undefined;
    };

    const handleCaptchaVerify = (captcha: string | null) => {
        form.setFieldsValue({ captcha });
    };

    const handleOk = () => {
        form.validateFields((err: any, values: UserEmailChangeValues) => {
            if (!err) {
                updateUserEmail({
                    newEmail: values.email,
                    humanVerificationResponse: values.captcha,
                }).then(() => closeDialog());
            }
        });
    };

    return (
        <Modal
            variant="content"
            open
            onClose={closeDialog}
            title={t("User_Change_Email")}
            footer={
                <>
                    <Button onClick={closeDialog}>{t("Cancel")}</Button>
                    <Button variant="primary" onClick={handleOk}>
                        {t("Save")}
                    </Button>
                </>
            }
        >
            <Form>
                <StringInput
                    label={t("MyProfile_UserDetails_Email")}
                    id="email"
                    initialValue={originalEmail}
                    type="CREATE"
                    form={form}
                    validators={[
                        {
                            kind: "ruleset",
                            required: true,
                            whitespace: true,
                        },
                        {
                            kind: "custom",
                            validator: emailValidator,
                        },
                        {
                            kind: "custom",
                            validator: shouldNotMatchPreviousEmailAddr,
                        },
                    ]}
                />
                <Form.Item>
                    {getFieldDecorator("captcha", {
                        rules: [{ required: true }],
                    })(
                        <HCaptcha
                            size="normal"
                            sitekey={ENVIRONMENT.IMPERO_HCAPTCHA_SITE_KEY}
                            onVerify={handleCaptchaVerify}
                        />
                    )}
                </Form.Item>
            </Form>
        </Modal>
    );
}

const UserEmailChange = Form.create<UserEmailChangeProps>()(
    UserEmailChangeInternal
);
